import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import {useTranslation} from 'react-i18next';
import {
  Button,
  Modal,
  ModalActions,
  ModalText,
  ModalTitle,
} from '../components/common/ui';

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  danger?: boolean;
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn>(async () => false);

interface PendingConfirm extends ConfirmOptions {
  resolve: (confirmed: boolean) => void;
}

export function ConfirmProvider({children}: {children: ReactNode}) {
  const {t} = useTranslation();
  const [pending, setPending] = useState<PendingConfirm | null>(null);

  const confirm = useCallback<ConfirmFn>(
    options =>
      new Promise<boolean>(resolve => {
        setPending({...options, resolve});
      }),
    [],
  );

  const settle = (confirmed: boolean) => {
    pending?.resolve(confirmed);
    setPending(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Modal open={pending !== null} onClose={() => settle(false)}>
        {pending && (
          <>
            <ModalTitle>{pending.title}</ModalTitle>
            {pending.message && <ModalText>{pending.message}</ModalText>}
            <ModalActions>
              <Button variant="ghost" onClick={() => settle(false)}>
                {pending.cancelText ?? t('Cancel')}
              </Button>
              <Button
                variant={pending.danger ? 'danger' : 'primary'}
                onClick={() => settle(true)}
                autoFocus
              >
                {pending.confirmText ?? t('Confirm')}
              </Button>
            </ModalActions>
          </>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export const useConfirm = () => useContext(ConfirmContext);
